import React, { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Users, FileText, CreditCard, Shield, TrendingUp } from "lucide-react";
import { inr } from "@/lib/format";
import { toast } from "sonner";
import MetricCard from "@/components/business/MetricCard";
import PageHeader from "@/components/business/PageHeader";

export default function AdminPanel() {
  return (
    <Routes>
      <Route index element={<AdminHome />} />
      <Route path="users" element={<AdminUsers />} />
      <Route path="filings" element={<AdminFilings />} />
      <Route path="payments" element={<AdminPayments />} />
      <Route path="audit" element={<AdminAudit />} />
    </Routes>
  );
}

function AdminHome() {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);
  const tiles = [
    { l: "Users", v: stats.total_users, icon: Users },
    { l: "Chartered Accountants", v: stats.total_cas, icon: Users },
    { l: "Filings", v: stats.total_filings, icon: FileText },
    { l: "Submitted", v: stats.submitted_filings, icon: TrendingUp },
    { l: "Paid orders", v: stats.paid_orders, icon: CreditCard },
    { l: "Revenue", v: inr(stats.revenue_inr || 0), icon: TrendingUp },
  ];
  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader>
          <PageHeader.Title eyebrow="Platform">Admin Overview</PageHeader.Title>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground">Platform-wide stats, users, filings, payments and audit logs.</p>
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-4">
          {tiles.map(t => (
            <MetricCard key={t.l} label={t.l} value={t.v ?? 0} icon={t.icon} />
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);
  const setRole = async (id, role) => { await api.patch(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); load(); };
  const toggle = async (id) => { await api.patch(`/admin/users/${id}/toggle`); toast.success("Updated"); load(); };
  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader><PageHeader.Title>Users</PageHeader.Title></PageHeader>
        <Card className="mt-6 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Active</th><th></th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-border">
                  <td className="p-3 font-medium">{u.full_name}</td>
                  <td className="p-3">{u.email}</td>
                  <td className="p-3">
                    <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                      <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="taxpayer">Taxpayer</SelectItem>
                        <SelectItem value="ca">CA</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="support">Support</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 capitalize">{u.plan}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${u.is_active ? "bg-accent/10 text-accent" : "bg-muted text-muted-foreground"}`}>{u.is_active ? "Active" : "Disabled"}</span></td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => toggle(u.id)}>{u.is_active ? "Disable" : "Enable"}</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}

function AdminFilings() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/filings").then(({ data }) => setRows(data)); }, []);
  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader><PageHeader.Title>All Filings</PageHeader.Title></PageHeader>
        <Card className="mt-6 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr><th className="text-left p-3">User</th><th className="text-left p-3">ITR</th><th className="text-left p-3">AY</th><th className="text-left p-3">Regime</th><th className="text-left p-3">Status</th><th className="text-left p-3">Updated</th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 font-mono text-xs">{r.user_id.slice(0, 8)}…</td>
                  <td className="p-3 font-medium">{r.itr_type}</td>
                  <td className="p-3">{r.assessment_year}</td>
                  <td className="p-3 capitalize">{r.regime}</td>
                  <td className="p-3 capitalize">{r.status}</td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(r.updated_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}

function AdminPayments() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/payments").then(({ data }) => setRows(data)); }, []);
  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader><PageHeader.Title>Payments</PageHeader.Title></PageHeader>
        <Card className="mt-6 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr><th className="text-left p-3">Order</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Status</th><th className="text-left p-3">Mode</th><th className="text-left p-3">When</th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 font-mono text-xs">{r.order_id}</td>
                  <td className="p-3 capitalize">{r.plan_code}</td>
                  <td className="p-3 num-rupee">{inr((r.amount || 0) / 100)}</td>
                  <td className="p-3 capitalize">{r.status}</td>
                  <td className="p-3 text-xs">{r.mock ? "Mock" : "Razorpay"}</td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}

function AdminAudit() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then(({ data }) => setRows(data)); }, []);
  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <PageHeader><PageHeader.Title>Audit Logs</PageHeader.Title></PageHeader>
        <Card className="mt-6 border-border overflow-hidden">
          <div className="divide-y divide-border">
            {rows.map(r => (
              <div key={r.id} className="p-4 flex items-center gap-4">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="font-mono text-xs text-foreground">{r.action}</div>
                  <div className="text-xs text-muted-foreground">{r.actor_email || "system"} · {new Date(r.at).toLocaleString()}</div>
                </div>
              </div>
            ))}
            {rows.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No audit events yet.</div>}
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
